import { useEffect, useMemo, useState } from 'react';
import { getSupabaseClient } from '../../lib/supabase';
import { subscribeSupabaseStudentRuntime } from '../../features/runtime/supabaseAdapter';

type StudentRow = { id: string; student_code: string; display_name: string; status: string };
type ProgressRow = { skill_id: string; mastery_state: string; mastery_percent: number; evidence_count: number; last_activity_at: string | null; average_active_seconds: number | null; average_hint_count: number | null };
type SkillRow = { id: string; code: string; learner_statement: string };

export function CloudTeacherStudentMonitorPage() {
  const [student, setStudent] = useState<StudentRow | null>(null);
  const [progress, setProgress] = useState<ProgressRow[]>([]);
  const [skills, setSkills] = useState<SkillRow[]>([]);
  const [realtimeCount, setRealtimeCount] = useState(0);
  const [error, setError] = useState('');

  async function load() {
    const client = getSupabaseClient();
    if (!client) throw new Error('Supabase belum dikonfigurasi.');
    const session = await client.auth.getSession();
    const profileId = session.data.session?.user.id;
    if (!profileId) throw new Error('Tutor belum login.');
    const teacher = await client.from('teachers').select('id').eq('profile_id', profileId).eq('status', 'active').single();
    if (teacher.error) throw teacher.error;
    const links = await client.from('teacher_students').select('student_id,subject_id').eq('teacher_id', teacher.data.id).eq('status', 'active').limit(1);
    if (links.error) throw links.error;
    const link = links.data?.[0];
    if (!link) throw new Error('Belum ada murid yang ditugaskan kepada tutor ini.');
    const s = await client.from('students').select('id,student_code,display_name,status').eq('id', link.student_id).single();
    if (s.error) throw s.error;
    const p = await client.from('student_skill_progress').select('skill_id,mastery_state,mastery_percent,evidence_count,last_activity_at,average_active_seconds,average_hint_count').eq('student_id', link.student_id).order('mastery_percent', { ascending: true });
    if (p.error) throw p.error;
    const ids = [...new Set((p.data ?? []).map((row) => String(row.skill_id)))];
    const k = ids.length ? await client.from('skills').select('id,code,learner_statement').in('id', ids) : { data: [], error: null };
    if (k.error) throw k.error;
    setStudent(s.data as StudentRow); setProgress((p.data ?? []) as ProgressRow[]); setSkills((k.data ?? []) as SkillRow[]);
    return String(link.student_id);
  }

  useEffect(() => {
    let disposed = false; let unsubscribe: () => void = () => undefined;
    void load().then((studentId) => {
      if (disposed) return;
      unsubscribe = subscribeSupabaseStudentRuntime(studentId, () => {
        setRealtimeCount((current) => current + 1);
        void load().catch((cause) => setError(cause instanceof Error ? cause.message : 'Realtime refresh gagal.'));
      });
    }).catch((cause) => setError(cause instanceof Error ? cause.message : 'Monitor tutor tidak dapat dimuat.'));
    return () => { disposed = true; unsubscribe(); };
  }, []);

  const skillMap = useMemo(() => new Map(skills.map((row) => [row.id, row])), [skills]);
  const attention = progress.filter((row) => row.mastery_percent < 60 || row.average_hint_count && row.average_hint_count >= 1);
  const lastActivity = progress.map((row) => row.last_activity_at).filter(Boolean).sort().at(-1);

  if (error) return <div className="feedback feedback--error">{error}</div>;
  if (!student) return <section className="card">Menyiapkan monitor tutor…</section>;

  return (
    <div className="stack">
      <div><h1>Monitor Murid</h1><p className="muted">Supabase Realtime · akses dibatasi assignment tutor + subject.</p></div>
      <section className="card student-monitor-card">
        <div className="student-monitor-card__header"><div><div className="eyebrow">Murid terhubung</div><h2>{student.display_name}</h2><p className="muted">{student.student_code} · Matematika</p></div><span className="status-dot status-dot--in_progress">Realtime aktif · {realtimeCount} update</span></div>
        <div className="grid grid--3"><div className="metric"><span>Skill</span><strong>{progress.length}</strong></div><div className="metric"><span>Perlu perhatian</span><strong>{attention.length}</strong></div><div className="metric"><span>Aktivitas terakhir</span><strong>{lastActivity ? new Date(lastActivity).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }) : '—'}</strong></div></div>
      </section>
      <section className="card"><h2>Skill mastery</h2><div className="skill-list">{progress.map((row) => { const skill = skillMap.get(row.skill_id); return <div className="skill-row" key={row.skill_id}><div><strong>{skill?.learner_statement ?? skill?.code}</strong><div className="muted skill-code">{skill?.code}</div></div><div className="skill-mastery"><strong>{row.mastery_percent}%</strong><span className="muted">{row.mastery_state.replaceAll('_', ' ')} · evidence {row.evidence_count}</span></div></div>; })}</div></section>
      <section className="card"><h2>Perlu perhatian</h2>{attention.length ? <ul>{attention.map((row) => <li key={row.skill_id}>{skillMap.get(row.skill_id)?.code ?? row.skill_id} · {row.mastery_percent}% · hint avg {row.average_hint_count ?? 0}</li>)}</ul> : <p className="muted">Belum ada skill yang membutuhkan perhatian khusus.</p>}</section>
    </div>
  );
}
